import { Box, HStack, Spacer, Stack } from '@chakra-ui/react'
import type { Uppy, UppyEventMap, UppyFile } from '@uppy/core'
import '@uppy/core/dist/style.css'
import '@uppy/status-bar/dist/style.css'
import '@uppy/file-input/dist/style.css'
import '@uppy/drop-target/dist/style.css'
import DropTarget from '@uppy/drop-target'
import StatusBar from '@uppy/status-bar'
import uniqueId from 'lodash/uniqueId'
import type { FC } from 'react'
import { useCallback } from 'react'
import { memo, useEffect, useRef, useState } from 'react'
import { FiUpload } from 'react-icons/fi'

import { Button } from '@app/components/ui/button'
import { useColorModeValue } from '@app/components/ui/color-mode'
import File from '@app/shared/uploader/components/file'
import type { FileTicketMeta, RemoteFile } from '@app/shared/uploader/types'
import { Sentry } from '@app/utils/sentry'
import { useOpenAiFileDeleteMutation } from '@graphql/queries'

type Props = {
  disabled?: boolean
  uppyInstance: Uppy<FileTicketMeta, RemoteFile>
  dropTargetRef?: React.RefObject<HTMLElement> | null
}

const MAX_FILES = 20 // OpenAI limit

type UppyFilesProps = {
  files: UppyFile<FileTicketMeta, RemoteFile>[]
  onDelete: Parameters<typeof File>[0]['onDelete']
}

const UppyFiles: FC<UppyFilesProps> = ({ files, onDelete }) => (
  <HStack wrap="wrap">
    {files.map((file) => (
      <File key={file.id} file={file} onDelete={onDelete} />
    ))}
  </HStack>
)

type Response = Parameters<UppyEventMap<FileTicketMeta, RemoteFile>['upload-success']>[1]

const DoubleloopUploader: FC<Props> = ({ disabled = false, uppyInstance, dropTargetRef = null }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const statusBarRef = useRef<HTMLDivElement>(null)
  const [pairs, setPairs] = useState<[string, string][]>([])
  const [, deleteFile] = useOpenAiFileDeleteMutation()
  const uppyTheme = useColorModeValue('light', 'dark')

  const maxRemainingFiles = MAX_FILES - uppyInstance.getFiles().length

  const accept = uppyInstance?.opts.restrictions ? uppyInstance?.opts.restrictions.allowedFileTypes?.join(',') : '*/*'

  useEffect(() => {
    const dropTargetId = uniqueId('drop-target')
    if (dropTargetRef.current) {
      uppyInstance.use(DropTarget, {
        id: dropTargetId,
        target: dropTargetRef.current,
        onDragOver: (e: DragEvent) => {
          const t = e.target as HTMLElement
          t.classList.add('uppy-drag-over')
        },
        onDragLeave: (e: DragEvent) => {
          const t = e.target as HTMLElement
          t?.classList.remove('uppy-drag-over')
        },
        onDrop: (e: DragEvent) => {
          const t = e.target as HTMLElement
          t?.classList.remove('uppy-drag-over')
        }
      })
    }

    const statusBarId = uniqueId('status-bar')
    if (statusBarRef.current) {
      uppyInstance.use(StatusBar, {
        id: statusBarId,
        target: statusBarRef.current,
        showProgressDetails: true,
        hideUploadButton: true
      })
    }

    return () => {
      if (dropTargetId) {
        const plugin = uppyInstance.getPlugin(dropTargetId)
        uppyInstance.removePlugin(plugin)
      }
      if (statusBarId) {
        const plugin = uppyInstance.getPlugin(statusBarId)
        uppyInstance.removePlugin(plugin)
      }
    }
  }, [dropTargetRef, uppyInstance])

  useEffect(() => {
    const newOnSuccess = (file: UppyFile<FileTicketMeta, RemoteFile>, response: Response) => {
      setPairs((prevPairs) => [...prevPairs, [file.id, response.body.id]])
    }

    uppyInstance.on('upload-success', newOnSuccess)

    return () => {
      uppyInstance.off('upload-success', newOnSuccess)
    }
  }, [uppyInstance])

  const onDelete = useCallback(
    async (file: UppyFile<FileTicketMeta, RemoteFile>) => {
      uppyInstance.removeFile(file.id)

      const pair = pairs.find(([uppyFileId, _]) => uppyFileId === file.id)
      if (!pair) {
        return
      }

      setPairs((prevPairs) => prevPairs.filter(([uppyFileId, _]) => uppyFileId !== file.id))
      return deleteFile({
        input: { fileId: pair[1] }
      })
    },
    [deleteFile, pairs, uppyInstance]
  )

  return (
    <>
      <Stack w="full">
        <HStack alignItems="flex-start">
          <UppyFiles files={uppyInstance.getFiles()} onDelete={onDelete} />
          <Spacer />
          <Button
            colorPalette="gray"
            disabled={disabled || maxRemainingFiles === 0}
            onClick={() => {
              if (inputRef?.current) {
                inputRef.current.click()
              }
            }}
            size="xs"
            type="button"
            variant="outline"
          >
            <FiUpload /> Upload files
          </Button>
        </HStack>
        <Box w="full" data-uppy-theme={uppyTheme}>
          <Box w="full" ref={statusBarRef} my={2} />
        </Box>
      </Stack>
      <input
        id="uppy-input"
        type="file"
        multiple
        accept={accept}
        ref={inputRef}
        style={{ display: 'none' }}
        onClick={(e) => {
          // Allow the same file to be uploaded again after it has been removed
          e.currentTarget.value = null
        }}
        onChange={(event) => {
          const uploadedFiles = Array.from(event.target.files)

          uploadedFiles.forEach((file) => {
            try {
              uppyInstance.addFile({
                source: 'file input',
                name: file.name,
                type: file.type,
                data: file
              })
            } catch (err) {
              Sentry.captureException(err)
            }
          })
        }}
      />
    </>
  )
}

export default memo(DoubleloopUploader)
